import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShieldCheck,
    UserCircle2,
    ChevronRight,
    Bot,
    LineChart,
    Wallet,
    ClipboardList,
    MapPin,
    BellRing,
    Users2,
    UserPlus,
    ExternalLink,
    Heart,
    Zap,
} from 'lucide-react';
import LanguageToggle from '@/components/LanguageToggle';
import { useLang } from '@/i18n/LangContext';

/**
 * MobileWelcome — Native app landing screen (Capacitor Android / iOS).
 * Restored to the "01:00 AM" design the user preferred:
 *   • Hero — bold left-aligned heading with "solar" in emerald, solar-house
 *     image on the right, floating white "Go Solar. Save More" pill overlay
 *     at bottom-left
 *   • Customer role card — GREEN accent (emerald), circular avatar, feature pills
 *   • Admin/Staff role card — BLUE accent (indigo), same shape
 *   • Signup CTA — dashed border with UserPlus icon and underlined green (Customer)
 *   • Trust badges — PM Surya Ghar · Har Payment Digital · Bharat BillPay
 *     inside a rounded card with vertical dividers between logos
 *   • Secure pill — slate/emerald with green shield
 *   • Made with ♥ in India, then a small footer with website link
 */
export default function MobileWelcome() {
    const nav = useNavigate();
    const { lang } = useLang();
    const [year] = useState(new Date().getFullYear());

    const openWebsite = () => {
        const url = 'https://upsolar.in';
        try {
            if (window?.Capacitor?.isNativePlatform?.() && window?.open) {
                window.open(url, '_system');
                return;
            }
        } catch { /* ignore */ }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => { document.title = 'UPPower Solar — PM Surya Ghar'; }, []);

    const customerFeatures = [
        { icon: ClipboardList, label: lang === 'hi' ? 'आवेदन ट्रैकिंग' : 'Application tracking' },
        { icon: Wallet, label: lang === 'hi' ? 'सब्सिडी + पेमेंट' : 'Subsidy & payments' },
        { icon: LineChart, label: lang === 'hi' ? 'लाइव जनरेशन' : 'Live generation' },
        { icon: Bot, label: lang === 'hi' ? 'AI सहायता' : 'AI assistant' },
    ];

    const staffFeatures = [
        { icon: Users2, label: lang === 'hi' ? 'लीड्स + कोटेशन' : 'Leads & quotations' },
        { icon: MapPin, label: lang === 'hi' ? 'साइट सर्वे' : 'Site surveys' },
        { icon: BellRing, label: lang === 'hi' ? 'नोटिफिकेशन' : 'Notifications' },
        { icon: Users2, label: lang === 'hi' ? 'अटेंडेंस + टीम' : 'Attendance & team' },
    ];

    return (
        <div
            className='min-h-screen w-full flex flex-col bg-gradient-to-b from-sky-50/60 via-background to-background text-foreground'
            data-testid='mobile-welcome'
        >
            {/* ─── Header ─────────────────────────────────────── */}
            <header className='px-5 pt-5 pb-2 flex items-center justify-between'>
                <img
                    src='/uppower-logo.png'
                    alt='UPPower Solar'
                    className='h-9 w-auto select-none'
                    draggable='false'
                />
                <LanguageToggle />
            </header>

            {/* ─── Hero: full-width text + badge, no image ─── */}
            <section
                className='relative px-5 pt-3 pb-6 overflow-hidden'
                data-testid='mobile-welcome-hero'
            >
                <h1 className='text-[30px] sm:text-[34px] font-bold leading-[1.12] tracking-tight'
                    style={{ color: '#08265F' }}>
                    {lang === 'hi' ? (
                        <>अपनी छत पर लगाएँ <span style={{ color: '#FF6B15' }}>सौर ऊर्जा</span>.<br/>बचत आज से।</>
                    ) : (
                        <>Power your home with <span style={{ color: '#FF6B15' }}>solar</span>.<br/>Save from day one.</>
                    )}
                </h1>
                <p className='mt-3 text-[14px] text-slate-600 leading-relaxed max-w-[340px]'>
                    {lang === 'hi'
                        ? 'सब्सिडी, आवेदन, बिल, और AI सहायता — सब एक ऐप में।'
                        : 'Subsidy, applications, bills, and AI help — all in one app.'}
                </p>

                <div className='mt-4 flex items-center gap-3 flex-wrap'>
                    {/* Accent bar */}
                    <div className='h-1 w-16 rounded-full shrink-0' style={{ background: '#FF6B15' }} />

                    {/* Go Solar badge — clean pill, no floating overlay */}
                    <div className='inline-flex items-center gap-2 rounded-full bg-white shadow-sm border border-slate-200 pl-1 pr-3.5 py-1'
                         data-testid='hero-badge'>
                        <div className='w-7 h-7 rounded-full grid place-items-center shrink-0' style={{ background: '#FFEBDC' }}>
                            <Zap className='w-4 h-4' style={{ color: '#FF6B15' }} fill='currentColor' />
                        </div>
                        <div className='leading-tight'>
                            <div className='text-[12px] font-bold' style={{ color: '#08265F' }}>Go Solar. Save More.</div>
                            <div className='text-[9.5px] text-slate-500'>Clean Energy, Better Tomorrow.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Role Cards ─────────────────────────────────── */}
            <section className='px-5 pt-2 pb-2 space-y-3'>
                {/* Customer */}
                <button
                    onClick={() => nav('/customer/login')}
                    className='w-full text-left rounded-2xl bg-card shadow-sm ring-1 ring-black/5 hover:ring-orange-200 active:scale-[0.995] transition-all overflow-hidden focus:outline-none focus-visible:ring-2'
                    style={{ '--tw-ring-color': '#FF6B15' }}
                    data-testid='mobile-welcome-customer'
                >
                    <div className='p-3.5 flex items-center gap-3'>
                        <div className='w-12 h-12 rounded-full grid place-items-center shadow-md shrink-0'
                             style={{ background: 'linear-gradient(135deg, #FF8A3D, #FF6B15)' }}>
                            <UserCircle2 className='w-7 h-7 text-white' strokeWidth={1.75} />
                        </div>
                        <div className='flex-1 min-w-0'>
                            <p className='text-[16px] font-semibold leading-tight' style={{ color: '#08265F' }}>
                                {lang === 'hi' ? 'मैं Customer हूँ' : 'I am a Customer'}
                            </p>
                            <p className='text-[12px] text-slate-500 mt-0.5 leading-snug'>
                                {lang === 'hi'
                                    ? 'आवेदन, सब्सिडी, बिल और बहुत कुछ ट्रैक करें'
                                    : 'Track application, subsidy, bills & more'}
                            </p>
                        </div>
                        <div className='w-9 h-9 rounded-full grid place-items-center shrink-0 shadow-sm'
                             style={{ background: '#FF6B15' }}>
                            <ChevronRight className='w-5 h-5 text-white' strokeWidth={2.5} />
                        </div>
                    </div>
                    <div className='px-3.5 pb-3.5 flex flex-wrap gap-1.5'>
                        {customerFeatures.map(({ icon: Icon, label }) => (
                            <span
                                key={label}
                                className='inline-flex items-center gap-1.5 rounded-full text-[11px] px-2.5 py-1'
                                style={{ background: '#FFF3E9', border: '1px solid #FFDCC1', color: '#B8460E' }}
                            >
                                <Icon className='w-3 h-3' style={{ color: '#FF6B15' }} />
                                {label}
                            </span>
                        ))}
                    </div>
                </button>

                {/* Admin / Staff */}
                <button
                    onClick={() => nav('/admin/login')}
                    className='w-full text-left rounded-2xl bg-card shadow-sm ring-1 ring-black/5 hover:ring-blue-200 active:scale-[0.995] transition-all overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400'
                    data-testid='mobile-welcome-admin'
                >
                    <div className='p-3.5 flex items-center gap-3'>
                        <div className='w-12 h-12 rounded-full grid place-items-center shadow-md shrink-0'
                             style={{ background: 'linear-gradient(135deg, #1e3a8a, #08265F)' }}>
                            <ShieldCheck className='w-7 h-7 text-white' strokeWidth={1.75} />
                        </div>
                        <div className='flex-1 min-w-0'>
                            <p className='text-[16px] font-semibold leading-tight' style={{ color: '#08265F' }}>
                                {lang === 'hi' ? 'मैं Admin / Staff हूँ' : 'I am Admin / Staff'}
                            </p>
                            <p className='text-[12px] text-slate-500 mt-0.5 leading-snug'>
                                {lang === 'hi'
                                    ? 'लीड्स, टीम और operations manage करें'
                                    : 'Manage leads, teams & operations'}
                            </p>
                        </div>
                        <div className='w-9 h-9 rounded-full grid place-items-center shrink-0 shadow-sm'
                             style={{ background: '#08265F' }}>
                            <ChevronRight className='w-5 h-5 text-white' strokeWidth={2.5} />
                        </div>
                    </div>
                    <div className='px-3.5 pb-3.5 flex flex-wrap gap-1.5'>
                        {staffFeatures.map(({ icon: Icon, label }) => (
                            <span
                                key={label}
                                className='inline-flex items-center gap-1.5 rounded-full text-[11px] px-2.5 py-1'
                                style={{ background: '#EEF3FA', border: '1px solid #D1DEF0', color: '#08265F' }}
                            >
                                <Icon className='w-3 h-3' style={{ color: '#08265F' }} />
                                {label}
                            </span>
                        ))}
                    </div>
                </button>
            </section>

            {/* ─── Signup CTA ─────────────────────────────────── */}
            <section className='px-5 pb-3'>
                <button
                    onClick={() => nav('/customer/login?tab=signup')}
                    className='w-full min-h-[46px] rounded-2xl border border-dashed bg-card text-[13.5px] font-medium flex items-center justify-center gap-2 active:scale-[0.995] transition-transform hover:border-orange-400'
                    style={{ borderColor: '#CBD5E1', color: '#08265F' }}
                    data-testid='mobile-welcome-signup'
                >
                    <UserPlus className='w-4 h-4' style={{ color: '#08265F' }} />
                    {lang === 'hi' ? (
                        <>नया अकाउंट बनाएँ <span className='font-semibold underline underline-offset-2' style={{ color: '#FF6B15' }}>(Customer)</span></>
                    ) : (
                        <>Create a new account <span className='font-semibold underline underline-offset-2' style={{ color: '#FF6B15' }}>(Customer)</span></>
                    )}
                </button>
            </section>

            {/* ─── Trust Badges Row ─── */}
            <section className='px-5 pb-3' data-testid='pmsgy-official-logo-block'>
                <div className='rounded-2xl bg-card shadow-sm ring-1 ring-black/5 px-3 py-3 flex items-center justify-around gap-2'>
                    <div className='flex-1 flex items-center justify-center h-14'>
                        <img
                            src='/pm-surya-ghar-logo.png'
                            alt='PM Surya Ghar: Muft Bijli Yojana'
                            className='max-h-full max-w-full object-contain select-none'
                            draggable='false'
                            data-testid='logo-pmsgy'
                        />
                    </div>
                    <div className='w-px h-10 bg-slate-200' />
                    <div className='flex-1 flex items-center justify-center h-14'>
                        <img
                            src='/logos/har-payment-digital.png'
                            alt='Har Payment Digital'
                            className='max-h-full max-w-full object-contain select-none'
                            draggable='false'
                            data-testid='logo-har-payment-digital'
                        />
                    </div>
                    <div className='w-px h-10 bg-slate-200' />
                    <div className='flex-1 flex items-center justify-center h-14'>
                        <img
                            src='/logos/bharat-billpay.png'
                            alt='Bharat BillPay'
                            className='max-h-full max-w-full object-contain select-none'
                            draggable='false'
                            data-testid='logo-bharat-billpay'
                        />
                    </div>
                </div>
            </section>

            {/* ─── Secure pill (logo navy) ─── */}
            <section className='px-5 pb-2'>
                <div className='rounded-2xl border px-3.5 py-2.5 flex items-center justify-center gap-2.5'
                     style={{ background: '#F0F4FA', borderColor: '#D1DEF0' }}>
                    <div className='w-7 h-7 rounded-full grid place-items-center shrink-0' style={{ background: '#FFEBDC' }}>
                        <ShieldCheck className='w-4 h-4' style={{ color: '#FF6B15' }} />
                    </div>
                    <p className='text-[12.5px] font-semibold leading-tight' style={{ color: '#08265F' }}>
                        {lang === 'hi' ? 'सुरक्षित • JWT ऑथ • डेटा एन्क्रिप्टेड' : 'Secure • JWT Auth • Data encrypted'}
                    </p>
                </div>
            </section>

            {/* ─── Made with ♥ in India ─── */}
            <section className='px-5 pb-2 flex justify-center' data-testid='made-in-india'>
                <p className='inline-flex items-center gap-1.5 text-[12px] text-slate-500'>
                    Made with
                    <Heart className='w-3.5 h-3.5 fill-red-500 text-red-500' />
                    in
                    <span className='font-bold' style={{ color: '#08265F' }}>India</span>
                </p>
            </section>

            {/* ─── Footer ─── */}
            <footer className='mt-auto px-5 py-3 border-t border-border/60 flex items-center justify-between'>
                <button
                    onClick={openWebsite}
                    className='inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground hover:text-foreground'
                    data-testid='mobile-welcome-open-website'
                >
                    <ExternalLink className='w-3.5 h-3.5' />
                    {lang === 'hi' ? 'हमारी वेबसाइट देखें' : 'Visit our website'}
                </button>
                <p className='text-[10.5px] text-muted-foreground'>© {year} UPPower Solar</p>
            </footer>
        </div>
    );
}
